import { useState, useEffect, useMemo } from "react";
import { toast } from 'sonner';
import { ScheduleMeetingDialog } from "./ScheduleMeetingDialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { cn } from "./ui/utils";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Progress } from "./ui/progress";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import matchingService from '../services/matchingService';
import connectionService from '../services/connectionService';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "./ui/table";
import { Checkbox } from "./ui/checkbox";
import { StarRating } from "./reviews/StarRating";
import { ReviewForm } from "./reviews/ReviewForm";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Sparkles,
  Target,
  TrendingUp,
  Award,
  MapPin,
  DollarSign,
  Users,
  CheckCircle,
  Star,
  MessageSquare,
  Calendar as CalendarIcon,
  Filter,
  ThumbsUp,
  Shield,
  Briefcase,
  GraduationCap,
  Heart,
  AlertCircle,
  X,
  RotateCcw,
  Clock,
  Share2,
  FileText,
  Plus,
} from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "./ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

import meetingService from "../services/meetingService";
import socialService from "../services/socialService";
import { EngagementAgreementDialog } from "./Agreement";
import engagementService from "../services/engagementService";
import { useAuth } from "../hooks/useAuth";
import { MultiSelect } from "./ui/multi-select";
import {
  SPECIALIZATION_FILTER_OPTIONS,
  LOCATION_FILTER_OPTIONS,
  CERTIFICATION_FILTER_OPTIONS,
  SECTOR_FILTER_OPTIONS,
  RATING_FILTER_OPTIONS,
  MATCH_SCORE_SLIDER,
  formatFeeTypeLabel,
  BILLING_FREQUENCY,
} from "../utils/financialTaxonomy";
import {
  MatchingCriteria,
  FILTER_PRESETS,
  createMatchingCriteriaFromFilters,
  countActiveFilters,
  formatCriteriaDescription,
  resetCriteria,
} from "../interface/MatchingCriteria";
import type { AdvisorProfile } from "../interface/AdvisorProfile";
import type { InvestorProfile } from "../interface/InvestorProfile";
import type { AnalystProfile } from "../interface/AnalystProfile";
import { FeeStructureOption } from "../interface/profile/FeeStructureOption";
import { PricingInfo } from "@/interface/PricingInfo";
import { AgreedFeeStructure } from "@/interface/AgreedFeeStructure";

// Helper function to convert FeeStructureOption feeType to financialTaxonomy fee type
const mapFeeTypeToTaxonomy = (feeType: string): string => {
  const mapping: Record<string, string> = {
    'AUM_BASED': 'aum',
    'PERFORMANCE_BASED': 'performance',
    'COMMISSION': 'commission',
    'FLAT_FEE': 'flat_fee',
    'HOURLY': 'hourly',
    'HYBRID': 'hybrid',
    'PROJECT_BASED': 'project',
    'SUBSCRIPTION': 'subscription',
    'RETAINER': 'retainer',
    'FREEMIUM': 'freemium',
    'PER_REQUEST': 'per_request',
    'PER_REPORT': 'per_report',
    'DATA_FEED': 'data_feed',
    'API_ACCESS': 'api_access',
  };
  return mapping[feeType] || feeType.toLowerCase();
};

export function RecommendationMatching() {
  const { userId, userRole } = useAuth();
  const [viewMode, setViewMode] = useState<"advisor" | "investor" | "analyst">("advisor");
  const [selectedMatch, setSelectedMatch] = useState<AdvisorProfile | InvestorProfile | AnalystProfile | null>(null);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [isEngagementDialogOpen, setIsEngagementDialogOpen] = useState(false);
  const [professionalToSchedule, setProfessionalToSchedule] = useState<AdvisorProfile | AnalystProfile | InvestorProfile | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [meetings, setMeetings] = useState<any[]>([]);
  const [connections, setConnections] = useState<any[]>([]);
  const [pendingMeetingRequests, setPendingMeetingRequests] = useState<any[]>([]);
  const [acceptedMeetingRequests, setAcceptedMeetingRequests] = useState<any[]>([]);
  const [ratingTarget, setRatingTarget] = useState<{ id: string; name: string; role: string } | null>(null);
  const [showOnlyEngaged, setShowOnlyEngaged] = useState(false);

  const filteredPendingRequests = useMemo(() => 
    pendingMeetingRequests.filter(meeting => meeting.hostId !== userId),
    [pendingMeetingRequests, userId]
  );
  
  // Engagement Requests State
  const [pendingAgreements, setPendingAgreements] = useState<any[]>([]);
  const [signedAgreements, setSignedAgreements] = useState<any[]>([]);
  const [isAgreementsLoading, setIsAgreementsLoading] = useState(false);

  // Connection Requests State
  const [pendingConnections, setPendingConnections] = useState<any[]>([]);
  const [acceptedConnections, setAcceptedConnections] = useState<any[]>([]);
  const [isRequestsLoading, setIsRequestsLoading] = useState(false);
  const [connectionMessage, setConnectionMessage] = useState("");
  const [selectedContactMethods, setSelectedContactMethods] = useState<string[]>(["email"]);
  const [sharePortfolio, setSharePortfolio] = useState(false);
  const [shareProfile, setShareProfile] = useState(false);
  const [isConnectDialogOpen, setIsConnectDialogOpen] = useState(false);
  const [isSharePortfolioDialogOpen, setIsSharePortfolioDialogOpen] = useState(false);
  const [sharingConnectionId, setSharingConnectionId] = useState<string | null>(null);
  const [sharingProfessionalId, setSharingProfessionalId] = useState<string | null>(null);
  const [sharingProfessionalName, setSharingProfessionalName] = useState("");
  const [isCurrentlyShared, setIsCurrentlyShared] = useState(false);

  const CONTACT_METHOD_OPTIONS = [
    { label: "Email", value: "email" },
    { label: "Phone", value: "phone" },
    { label: "Video Call", value: "video" },
    { label: "Messaging", value: "messaging" },
    { label: "In Person", value: "in_person" },
  ];

  const handleSendConnectionRequest = async (recipientId: string) => {
    if (!userId) {
      toast.error("Please log in to send connection requests");
      return;
    }

    if (!connectionMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }

    if (selectedContactMethods.length === 0) {
      toast.error("Please select at least one contact method");
      return;
    }

    try {
      await connectionService.sendConnectionRequest({
        requesterId: userId,
        recipientId: recipientId,
        message: connectionMessage,
        preferredContactMethods: selectedContactMethods,
        sharePortfolio: userRole === 'INVESTOR' ? sharePortfolio : false,
        shareProfile: shareProfile,
        status: 'PENDING'
      });
      toast.success("Connection request sent!");
      setConnectionMessage("");
      setSelectedContactMethods(["email"]);
      setSharePortfolio(false);
      setShareProfile(false);
      setIsConnectDialogOpen(false);
      fetchConnectionRequests();
    } catch (error: any) {
      toast.error(error.message || "Failed to send connection request");
    }
  };

  const fetchConnectionRequests = async () => {
    if (!userId) return;
    try {
      setIsRequestsLoading(true);
      const [pendingRes, acceptedRes] = await Promise.all([
        //connectionService.getPendingRequests(userId),
        connectionService.getConnections(userId, { status: 'PENDING' }),
        connectionService.getConnections(userId, { status: 'ACTIVE' })
      ]);
      
      const pendingResponse = pendingRes.data || (Array.isArray(pendingRes.connections) ? pendingRes.connections : []);
      const acceptedResponse = acceptedRes.data || (Array.isArray(acceptedRes.connections) ? acceptedRes.connections : []);
      
      setPendingConnections(pendingResponse);
      setAcceptedConnections(acceptedResponse);
      
      const allConnections = [...pendingResponse, ...acceptedResponse].sort((a, b) => 
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );  
      setConnections(allConnections);
    } catch (error) {
      console.error("Error fetching connection requests:", error);
    } finally {
      setIsRequestsLoading(false);
    }
  };

  const handleAcceptConnection = async (requestId: string) => {
    if (!userId) {
      toast.error("User not authenticated");
      return;
    }
    try {
      await connectionService.acceptConnectionRequest(userId, requestId);
      toast.success("Connection request accepted");
      fetchConnectionRequests();
    } catch (error) {
      toast.error("Failed to accept connection request");
    }
  };

  const handleRejectConnection = async (requestId: string) => {
    if (!userId) {
      toast.error("User not authenticated");
      return;
    }
    try {
      await connectionService.rejectConnectionRequest(userId, requestId);
      toast.success("Connection request rejected");
      fetchConnectionRequests();
    } catch (error) {
      toast.error("Failed to reject connection request");
    }
  };

  const handleRemoveConnection = async (connectionId: string) => {
    if (!userId) {
      toast.error("User not authenticated");
      return;
    }
    try {
      await connectionService.removeConnection(userId, connectionId);
      toast.success("Connection removed");
      fetchConnectionRequests();
    } catch (error) {
      toast.error("Failed to remove connection");
    }
  };

  const fetchAgreements = async () => {
    if (!userId) return;
    try {
      setIsAgreementsLoading(true);
      const response = await engagementService.getMyAgreements(userId);
      const agreements = response.data || (Array.isArray(response) ? response : []);
      setPendingAgreements(
        agreements.filter((a: any) => a.status === 'PENDING' || a.status === 'SIGNED_BY_RECIPIENT')
      );
      setSignedAgreements(
        agreements.filter((a: any) => a.status === 'SIGNED_BY_BOTH')
      );
    } catch (error) {
      console.error("Error fetching agreements:", error);
    } finally {
      setIsAgreementsLoading(false);
    }
  };

  const buildProfessionalFromAgreement = (ag: any): AdvisorProfile => ({
    id: String(ag.requesterId === userId ? ag.recipientId : ag.requesterId),
    name: ag.requesterId === userId ? (ag.recipientFullName || "Professional") : (ag.requesterFullName || "Professional"),
    title: "",
    location: "",
    matchScore: 0,
    experience: "",
    aum: "",
    specializations: [],
    certifications: [],
    clientTypes: [],
    minInvestment: "",
    rating: 0,
    reviewCount: 0,
    avatar: "",
    bio: "",
    feeStructureOptions: [],
    pricingInfo: [],
  });

  const handleCancelConnection = async (connectionId: string) => {
    if (!userId) {
      toast.error("User not authenticated");
      return;
    }
    try {
      await connectionService.cancelConnection(userId, connectionId);
      toast.success("Connection cancelled");
      fetchConnectionRequests();
    } catch (error) {
      toast.error("Failed to cancel connection request");
    }
    };

    const handleSharePortfolio = async () => {
    if (!sharingProfessionalId || !sharingConnectionId) return;

    try {
      const newStatus = !isCurrentlyShared;
      await connectionService.sharePortfolio(sharingProfessionalId, sharingConnectionId, newStatus);
      toast.success(newStatus ? "Portfolio shared successfully" : "Portfolio unshared successfully");
      setIsSharePortfolioDialogOpen(false);
      fetchConnectionRequests();
    } catch (error: any) {
      toast.error(error.message || "Failed to update portfolio sharing status");
    }
    };

    const fetchMeetings = async () => {    if (!userId) return;
    try {
      const [pendingRes, acceptedRes] = await Promise.all([
        meetingService.getMyMeetings(userId, "PENDING", false,''),
        meetingService.getMyMeetings(userId, "ACCEPTED", false,'')
      ]);
      
      const pMeetings = pendingRes.data || (Array.isArray(pendingRes) ? pendingRes : []);
      const aMeetings = acceptedRes.data || (Array.isArray(acceptedRes) ? acceptedRes : []);
      
      setPendingMeetingRequests(pMeetings);
      setAcceptedMeetingRequests(aMeetings);
      
      const allMeetings = [...pMeetings, ...aMeetings].sort((a, b) => 
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      );
      
      setMeetings(allMeetings);
    } catch (error) {
      console.error("Error fetching meetings:", error);
    }
  };

  const handleAcceptMeeting = async (meetingId: string) => {
    if (!userId) return;
    try {
      await meetingService.updateMeetingStatus(meetingId, userId, "ACCEPTED");
      toast.success("Meeting request accepted");
      fetchMeetings();
    } catch (error: any) {
      toast.error(error.message || "Failed to accept meeting request");
    }
  };

  const handleDeclineMeeting = async (meetingId: string) => {
    if (!userId) return;
    try {
      await meetingService.updateMeetingStatus(meetingId, userId, "DECLINED");
      toast.success("Meeting request declined");
      fetchMeetings();
    } catch (error: any) {
      toast.error(error.message || "Failed to decline meeting request");
    }
  };

  const handleCancelMeeting = async (meetingId: string) => {
    if (!userId) return;
    try {
      await meetingService.updateMeetingStatus(meetingId, userId, "CANCELLED");
      toast.success("Meeting cancelled");
      fetchMeetings();
    } catch (error: any) {
      toast.error(error.message || "Failed to cancel meeting");
    }
  };

  useEffect(() => {
    fetchMeetings();
    fetchConnectionRequests();
    fetchAgreements();
  }, [userId]);
  
  // Filter states
  const [minMatchScore, setMinMatchScore] = useState([70]);
  const [selectedSpecialization, setSelectedSpecialization] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [customLocation, setCustomLocation] = useState("");
  const [minRating, setMinRating] = useState("0");
  const [selectedSpecializations, setSelectedSpecializations] = useState<string[]>([]);
  const [selectedCertifications, setSelectedCertifications] = useState<string[]>([]);
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);

  // Pending filter states (not yet applied)
  const [pendingMinMatchScore, setPendingMinMatchScore] = useState([70]);
  const [pendingSpecializations, setPendingSpecializations] = useState<string[]>([]);
  const [pendingCertifications, setPendingCertifications] = useState<string[]>([]);
  const [pendingSectors, setPendingSectors] = useState<string[]>([]);
  const [pendingLocation, setPendingLocation] = useState("all");
  const [pendingCustomLocation, setPendingCustomLocation] = useState("");
  const [pendingMinRating, setPendingMinRating] = useState("0");

  // Check if there are pending changes
  const hasPendingChanges = 
    JSON.stringify(pendingMinMatchScore) !== JSON.stringify(minMatchScore) ||
    JSON.stringify(pendingSpecializations) !== JSON.stringify(selectedSpecializations) ||
    JSON.stringify(pendingCertifications) !== JSON.stringify(selectedCertifications) ||
    JSON.stringify(pendingSectors) !== JSON.stringify(selectedSectors) ||
    pendingLocation !== selectedLocation ||
    pendingCustomLocation !== customLocation ||
    pendingMinRating !== minRating;

  // Reset all filters
  const handleResetFilters = () => {
    const defaults = {
      minMatchScore: [70],
      specializations: [],
      certifications: [],
      sectors: [],
      location: "all",
      customLocation: "",
      minRating: "0",
    };
    
    // Reset both pending and active states
    setPendingMinMatchScore(defaults.minMatchScore);
    setPendingSpecializations(defaults.specializations);
    setPendingCertifications(defaults.certifications);
    setPendingSectors(defaults.sectors);
    setPendingLocation(defaults.location);
    setPendingCustomLocation(defaults.customLocation);
    setPendingMinRating(defaults.minRating);
    
    setMinMatchScore(defaults.minMatchScore);
    setSelectedSpecializations(defaults.specializations);
    setSelectedCertifications(defaults.certifications);
    setSelectedSectors(defaults.sectors);
    setSelectedLocation(defaults.location);
    setCustomLocation(defaults.customLocation);
    setMinRating(defaults.minRating);
    
    toast.success("Filters reset to defaults");
  };

  // Apply pending filters
  const handleApplyFilters = () => {
    // Create criteria with pending values (not current state)
    const newCriteria = createMatchingCriteriaFromFilters({
      minMatchScore: pendingMinMatchScore,
      selectedSpecializations: pendingSpecializations,
      selectedCertifications: pendingCertifications,
      selectedSectors: pendingSectors,
      selectedLocation: pendingLocation,
      customLocation: pendingCustomLocation,
      minRating: pendingMinRating,
      role: localStorage.getItem('userRole') || userRole || undefined,
    });
    
    // Update state
    setMinMatchScore(pendingMinMatchScore);
    setSelectedSpecializations(pendingSpecializations);
    setSelectedCertifications(pendingCertifications);
    setSelectedSectors(pendingSectors);
    setSelectedLocation(pendingLocation);
    setCustomLocation(pendingCustomLocation);
    setMinRating(pendingMinRating);
    
    toast.success("Filters applied successfully");
  };

  // Clear pending changes (revert to active filters)
  const handleClearPendingChanges = () => {
    setPendingMinMatchScore(minMatchScore);
    setPendingSpecializations(selectedSpecializations);
    setPendingCertifications(selectedCertifications);
    setPendingSectors(selectedSectors);
    setPendingLocation(selectedLocation);
    setPendingCustomLocation(customLocation);
    setPendingMinRating(minRating);
  };

  // Apply filter preset
  const handleApplyPreset = (presetId: string) => {
    const preset = FILTER_PRESETS.find(p => p.id === presetId);
    if (!preset) return;

    const presetValues = {
      minMatchScore: [preset.criteria.minMatchScore || 70],
      specializations: preset.criteria.specializations || [],
      certifications: preset.criteria.requiredCertifications || [],
      sectors: preset.criteria.sectors || [],
      location: preset.criteria.location || "all",
      minRating: String(preset.criteria.minRating || 0),
    };

    // Set pending values from preset
    setPendingMinMatchScore(presetValues.minMatchScore);
    setPendingSpecializations(presetValues.specializations);
    setPendingCertifications(presetValues.certifications);
    setPendingSectors(presetValues.sectors);
    setPendingLocation(presetValues.location);
    setPendingMinRating(presetValues.minRating);
    
    // Also apply immediately
    setMinMatchScore(presetValues.minMatchScore);
    setSelectedSpecializations(presetValues.specializations);
    setSelectedCertifications(presetValues.certifications);
    setSelectedSectors(presetValues.sectors);
    setSelectedLocation(presetValues.location);
    setMinRating(presetValues.minRating);
    setShowOnlyEngaged(presetId === 'engaged');
    
    toast.success(`Applied preset: ${preset.name}`);
  };

  /* // Get current matching criteria
  const currentCriteria = createMatchingCriteriaFromFilters({
    minMatchScore,
    selectedSpecializations,
    selectedCertifications,
    selectedSectors,
    selectedLocation,
    customLocation,
    minRating,
  }); */

  // Memoize currentCriteria to prevent unnecessary recalculations
const currentCriteria = useMemo(() => {
  return createMatchingCriteriaFromFilters({
    minMatchScore,
    selectedSpecializations,
    selectedCertifications,
    selectedSectors,
    selectedLocation,
    customLocation,
    minRating,
    role: localStorage.getItem('userRole') || userRole || undefined,
  });
}, [
  minMatchScore,
  selectedSpecializations,
  selectedCertifications,
  selectedSectors,
  selectedLocation,
  customLocation,
  minRating,
  userRole,
]);
  const activeFilterCount = countActiveFilters(currentCriteria);
  const criteriaDescription = formatCriteriaDescription(currentCriteria);
  // NEW: API-related states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [advisorMatches, setAdvisorMatches] = useState<AdvisorProfile[]>([]);
  const [analystMatches, setAnalystMatches] = useState<AnalystProfile[]>([]);
  const [investorMatches, setInvestorMatches] = useState<InvestorProfile[]>([]);


  //========API CALL START========

  // Fetch advisor matches from backend
const fetchAdvisorMatches = async () => {
  if (isLoading) return; // Prevent duplicate calls
  
  setIsLoading(true);
  setError(null);
  
  try {
    // Call backend API: POST /api/matching/advisors
    const response = await matchingService.getAdvisorMatches(currentCriteria);
    console.log('Response data:', response);
    // Transform backend MatchResult to AdvisorProfile
    // Check if response has data property
    const dataToMap = response.data || response;
    const transformedAdvisors = dataToMap.map(transformMatchResultToAdvisor);
    setAdvisorMatches(transformedAdvisors);
  } catch (err: any) {
    const errorMessage =  err.response?.data?.message || "Failed to load advisor matches";
    setError(errorMessage);
    toast.error(errorMessage);
  } finally {
    setIsLoading(false);
  }
};

// Fetch analyst matches from backend
const fetchAnalystMatches = async () => {
  if (isLoading) return; // Prevent duplicate calls
  
  setIsLoading(true);
  setError(null);
  
  try {
    // Call backend API: POST /api/matching/analysts
    //const response = await matchingService.getMatchRecommendations(criteria);
    const response = await matchingService.getAnalystMatches(currentCriteria);
    
    // Transform backend MatchResult to AnalystProfile
    // Check if response has data property
    const dataToMap = response.data || response;
    const transformedAnalysts = dataToMap.map(transformMatchResultToAnalyst);
    setAnalystMatches(transformedAnalysts);
  } catch (err: any) {
    const errorMessage = err.response?.data?.message || "Failed to load analyst matches";
    setError(errorMessage);
    toast.error(errorMessage);
  } finally {
    setIsLoading(false);
  }
};

// Fetch investor matches (for advisors/analysts)
const fetchInvestorMatches = async () => {
  if (isLoading) return; // Prevent duplicate calls
  
  setIsLoading(true);
  setError(null);
  
  try {
    // Call backend API: GET /api/matching/comprehensive
    //const response = await matchingService.getComprehensiveMatches(criteria);
    const response = await matchingService.getInvestorMatches(currentCriteria);
    
    // Transform backend data to InvestorProfile
    // Check if response has data property
    const dataToMap = response.data || response;
    const transformedInvestors = dataToMap.map(transformMatchResultToInvestor) || [];
    setInvestorMatches(transformedInvestors);
  } catch (err: any) {
    const errorMessage = err.response?.data?.message || "Failed to load investor matches";
    setError(errorMessage);
    toast.error(errorMessage);
  } finally {
    setIsLoading(false);
  }
};

  //========API CALL END========

  //========TRANFORM MATCHRESULT START======
  //Transform backend `MatchResult` to frontend profile types:


// Transform backend MatchResult to AdvisorProfile
const transformMatchResultToAdvisor = (match: any): AdvisorProfile => {
  return {
    id: match.professionalId || match.id,
    name: match.professionalName || match.name,
    title: match.title || "Financial Advisor",
    location: match.location || match.address?.city || "Location N/A",
    matchScore: Math.round(Number(match.matchScore) || 0),
    experience: match.yearsExperience ? `${match.yearsExperience} years` : "N/A",
    aum: match.aum ? `$${(match.aum / 1000000).toFixed(1)}M` : "N/A",
    specializations: match.specializations || [],
    certifications: match.certifications || [],
    clientTypes: match.clientTypes || [],
    minInvestment: match.minimumInvestment ? `$${(match.minimumInvestment / 1000).toFixed(0)}K` : "N/A",
    rating: match.averageRating || 0,
    reviewCount: match.reviewCount || 0,
    avatar: getInitials(match.professionalName || match.name),
    bio: match.bio || "No bio available",
    pricingInfo: match.pricingInfo,
    feeStructureOptions: Array.isArray(match.feeStructureOptions) 
      ? match.feeStructureOptions.map((f: any) => ({
          id: f.id || String(Math.random()),
          name: f.name || "Default",
          feeType: f.feeType || "AUM_BASED",
          aumPercentage: f.aumPercentage,
          flatFeeAmount: f.flatFeeAmount,
          hourlyRate: f.hourlyRate,
          description: f.description || "Contact for details",
        }))
      : match.feeStructureOptions ? [{
          id: match.feeStructureOptions.id || String(Math.random()),
          name: match.feeStructureOptions.name || "Default",
          feeType: match.feeStructureOptions.feeType || "AUM_BASED",
          aumPercentage: match.feeStructureOptions.aumPercentage,
          flatFeeAmount: match.feeStructureOptions.flatFeeAmount,
          hourlyRate: match.feeStructureOptions.hourlyRate,
          description: match.feeStructureOptions.description || "Contact for details",
        }] : [],
  };
};

// Transform backend MatchResult to AnalystProfile
const transformMatchResultToAnalyst = (match: any): AnalystProfile => {
  return {
    id: match.professionalId || match.id,
    name: match.professionalName || match.name,
    title: match.title || "Financial Analyst",
    location: match.location || match.address?.city || "Location N/A",
    matchScore: Math.round(Number(match.matchScore) || 0),
    experience: match.yearsExperience ? `${match.yearsExperience} years` : "N/A",
    specializations: match.specializations || [],
    certifications: match.certifications || [],
    researchFocus: match.researchFocus || match.specializations || [],
    publications: match.publications || 0,
    rating: match.averageRating || 0,
    reviewCount: match.reviewCount || 0,
    avatar: getInitials(match.professionalName || match.name),
    bio: match.bio || "No bio available",
    pricingInfo: match.pricingInfo,
    feeStructureOptions: Array.isArray(match.feeStructureOptions) 
      ? match.feeStructureOptions.map((f: any) => ({
          id: f.id || String(Math.random()),
          name: f.name || "Default",
          feeType: f.feeType || "PER_REQUEST",
          perRequestAmount: f.perRequestAmount,
          subscriptionAmount: f.subscriptionFee,
          description: f.description || "Contact for details",
        }))
      : match.feeStructureOptions ? [{
          id: match.feeStructureOptions.id || String(Math.random()),
          name: match.feeStructureOptions.name || "Default",
          feeType: match.feeStructureOptions.feeType || "PER_REQUEST",
          perRequestAmount: match.feeStructureOptions.perRequestAmount,
          subscriptionAmount: match.feeStructureOptions.subscriptionFee,
          description: match.feeStructureOptions.description || "Contact for details",
        }] : []
  };
};

// Transform backend MatchResult to InvestorProfile
const transformMatchResultToInvestor = (match: any): InvestorProfile => {
  return {
    id: match.professionalId || match.id,
    name: match.professionalName || match.name,
    location: match.location || match.address?.city || "Location N/A",
    matchScore: Math.round(Number(match.matchScore) || 0),
    portfolioValue: match.portfolioValue ? `$${(match.portfolioValue / 1000).toFixed(0)}K` : "N/A",
    investmentGoals: match.investmentGoals || [],
    riskTolerance: match.riskTolerance || "Moderate",
    investmentHorizon: match.investmentHorizon || "N/A",
    preferredSectors: match.preferredSectors || [],
    avatar: getInitials(match.professionalName || match.name),
    lookingFor: match.lookingFor || "Seeking professional financial guidance",
  };
};

// Helper function to get initials
const getInitials = (name: string): string => {
  return name
    .split(" ")
    .map(n => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
};


// 1. Add a loading guard to prevent duplicate calls
const [isLoading, setIsLoading] = useState(false);

// Fetch data when viewMode or filters change
useEffect(() => {
  if (viewMode === "advisor" && !isLoading) {
    fetchAdvisorMatches();
  } else if (viewMode === "analyst" && !isLoading) {
    fetchAnalystMatches();
  } else if (viewMode === "investor" && !isLoading) {
    fetchInvestorMatches();
  }
}, [viewMode, currentCriteria]);

// Initial load
/* useEffect(() => {
  fetchAdvisorMatches();
}, []); */
  //========calling usereffet hooks: ends========

  const engagedUserIds = new Set([
    ...pendingAgreements.flatMap((a: any) => [a.professionalId, a.clientId].filter(Boolean)),
    ...signedAgreements.flatMap((a: any) => [a.professionalId, a.clientId].filter(Boolean)),
  ]);

  const filteredAdvisors = advisorMatches.filter(advisor => {
    const matchScoreFilter = advisor.matchScore >= minMatchScore[0];
    const specializationFilter = selectedSpecializations.length === 0 || 
      advisor.specializations.some(s => selectedSpecializations.includes(s.toLowerCase()));
    const certificationFilter = selectedCertifications.length === 0 || 
      advisor.certifications.some(c => selectedCertifications.includes(c.toLowerCase()));
    const locationFilter = selectedLocation === "all" || 
      advisor.location.toLowerCase().includes(selectedLocation.toLowerCase()) ||
      advisor.location.toLowerCase().includes(customLocation.toLowerCase());
    const ratingFilter = advisor.rating >= parseFloat(minRating);
    const engagementFilter = !showOnlyEngaged || engagedUserIds.has(advisor.id);
    
    return matchScoreFilter && specializationFilter && certificationFilter && locationFilter && ratingFilter && engagementFilter;
  });

  const filteredInvestors = investorMatches.filter(investor => {
    const matchScoreFilter = investor.matchScore >= minMatchScore[0];
    const sectorFilter = selectedSectors.length === 0 || 
      investor.preferredSectors.some(s => 
        selectedSectors.some(selected => s.toLowerCase().includes(selected.toLowerCase()))
      );
    const locationFilter = selectedLocation === "all" || 
      investor.location.toLowerCase().includes(selectedLocation.toLowerCase()) ||
      investor.location.toLowerCase().includes(customLocation.toLowerCase());
    
    const engagementFilter = !showOnlyEngaged || engagedUserIds.has(investor.id);
    return matchScoreFilter && sectorFilter && locationFilter && engagementFilter;
  });



  const filteredAnalysts = analystMatches.filter(analyst => {
    const matchScoreFilter = analyst.matchScore >= minMatchScore[0];
    const specializationFilter = selectedSpecializations.length === 0 || 
      analyst.specializations.some(s => selectedSpecializations.includes(s.toLowerCase()));
    const certificationFilter = selectedCertifications.length === 0 || 
      analyst.certifications.some(c => selectedCertifications.includes(c.toLowerCase()));
    const sectorFilter = selectedSectors.length === 0 || 
      analyst.researchFocus.some(s => 
        selectedSectors.some(selected => s.toLowerCase().includes(selected.toLowerCase()))
      );
    const locationFilter = selectedLocation === "all" || 
      analyst.location.toLowerCase().includes(selectedLocation.toLowerCase()) ||
      analyst.location.toLowerCase().includes(customLocation.toLowerCase());
    const ratingFilter = analyst.rating >= parseFloat(minRating);
    const engagementFilter = !showOnlyEngaged || engagedUserIds.has(analyst.id);
    
    return matchScoreFilter && specializationFilter && certificationFilter && sectorFilter && locationFilter && ratingFilter && engagementFilter;
  });

  return (
    <div className="space-y-6">
      {/* Connection Requests Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <CardTitle>Connection Requests</CardTitle>
          </div>
          <CardDescription>
            Manage your professional network and pending invitations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="pending">
            <TabsList className="mb-4">
              <TabsTrigger value="pending" className="relative">
                Pending
                {pendingConnections.length > 0 && (
                  <Badge className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center bg-primary text-primary-foreground text-[10px]">
                    {pendingConnections.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="accepted" className="relative">
                Accepted
                {acceptedConnections.length > 0 && (
                  <Badge className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center bg-primary text-primary-foreground text-[10px]">
                    {acceptedConnections.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="pending">
              {pendingConnections.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Requester</TableHead>
                      <TableHead>Requester Role</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Recipient Role</TableHead>
                      <TableHead>Request Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingConnections.map((req) => {
                      const isRequester = String(req.requesterId) === String(userId);
                      const otherName = isRequester ? req.recipientFullName : req.requesterFullName;
                      const otherRole = isRequester ? req.recipientRole : req.requesterRole;
                      const otherEmail = isRequester ? req.recipientEmail : req.requesterEmail;
                      
                      return (
                        <TableRow key={req.connectionId}>
                          <TableCell className="font-medium">
                            <div>
                              <p>{req.requesterFullName || "Unknown User"}</p>
                              <p className="text-xs text-muted-foreground font-normal">{req.requesterEmail}</p>
                            </div>
                          </TableCell>
                          <TableCell className="capitalize">
                            {req.requesterRole?.toLowerCase() || "N/A"}
                          </TableCell>
                          <TableCell className="font-medium">
                            <div>
                              <p>{req.recipientFullName || "Unknown User"}</p>
                              <p className="text-xs text-muted-foreground font-normal">{req.recipientEmail}</p>
                            </div>
                          </TableCell>
                          <TableCell className="capitalize">
                            {req.recipientRole?.toLowerCase() || "N/A"}
                          </TableCell>
                          <TableCell>
                            {req.createdAt ? format(new Date(req.createdAt), "MMM d, yyyy") : "N/A"}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                              {req.status || "PENDING"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              {!isRequester && (
                                <Button 
                                  size="sm" 
                                  onClick={() => handleAcceptConnection(req.connectionId)}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  Accept
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => isRequester ? handleCancelConnection(req.connectionId) : handleRejectConnection(req.connectionId)}
                                className="text-red-600 border-red-200 hover:bg-red-50"
                              >
                                {isRequester ? "Cancel" : "Reject"}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-8 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
                  <p>No pending connection requests</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="accepted">
              {acceptedConnections.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Requester</TableHead>
                      <TableHead>Requester Role</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Recipient Role</TableHead>
                      <TableHead>Connection Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {acceptedConnections.map((conn) => {
                      const isRequester = String(conn.requesterId) === String(userId);
                      const otherName = isRequester ? conn.recipientFullName : conn.requesterFullName;
                      const otherRole = isRequester ? conn.recipientRole : conn.requesterRole;
                      const otherEmail = isRequester ? conn.recipientEmail : conn.requesterEmail;
                      const otherCompany = isRequester ? conn.recipientCompany : conn.requesterCompany;
                      
                      return (
                        <TableRow key={conn.connectionId}>
                          <TableCell className="font-medium">
                            <div>
                              <p>{conn.requesterFullName || "Unknown User"}</p>
                              <p className="text-xs text-muted-foreground font-normal">{conn.requesterEmail}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="capitalize">{conn.requesterRole?.toLowerCase() || "N/A"}</span>
                              {conn.requesterCompany && <span className="text-xs text-muted-foreground">{conn.requesterCompany}</span>}
                            </div>
                          </TableCell>
                          
                          <TableCell className="font-medium">
                            <div>
                              <p>{conn.recipientFullName || "Unknown User"}</p>
                              <p className="text-xs text-muted-foreground font-normal">{conn.recipientEmail}</p>
                            </div>
                          </TableCell>
                          <TableCell className="capitalize">
                            {conn.recipientRole?.toLowerCase() || "N/A"}
                            {conn.recipientCompany && <span className="text-xs text-muted-foreground">{conn.recipientCompany}</span>}
                          </TableCell>
                          <TableCell>
                            {conn.connectedAt ? format(new Date(conn.connectedAt), "MMM d, yyyy") : (conn.createdAt ? format(new Date(conn.createdAt), "MMM d, yyyy") : "N/A")}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              {conn.status || "ACTIVE"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => handleRemoveConnection(conn.connectionId)}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              Remove
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-8 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
                  <p>No active connections</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Engagement Requests Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <CardTitle>Engagement Requests</CardTitle>
          </div>
          <CardDescription>
            Manage your professional engagement agreements and signed contracts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="pending">
            <TabsList className="mb-4">
              <TabsTrigger value="pending" className="relative">
                Pending
                {pendingAgreements.length > 0 && (
                  <Badge className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center bg-primary text-primary-foreground text-[10px]">
                    {pendingAgreements.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="signed" className="relative">
                Signed
                {signedAgreements.length > 0 && (
                  <Badge className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center bg-primary text-primary-foreground text-[10px]">
                    {signedAgreements.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending">
              {isAgreementsLoading ? (
                <div className="py-8 text-center text-muted-foreground">Loading agreements...</div>
              ) : pendingAgreements.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Requester</TableHead>
                      <TableHead>Requester Role</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Recipient Role</TableHead>
                      <TableHead>Scope</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingAgreements.map((ag) => {
                      const isRequester = String(ag.requesterId) === String(userId);
                      return (
                        <TableRow key={ag.id}>
                          <TableCell className="font-medium">
                            <div>
                              <p>{ag.requesterFullName || "Unknown"}</p>
                              <p className="text-xs text-muted-foreground font-normal">{ag.requesterEmail}</p>
                            </div>
                          </TableCell>
                          <TableCell className="capitalize">
                            {ag.requesterRole?.toLowerCase() || "N/A"}
                          </TableCell>
                          <TableCell className="font-medium">
                            <div>
                              <p>{ag.recipientFullName || "Unknown"}</p>
                              <p className="text-xs text-muted-foreground font-normal">{ag.recipientEmail}</p>
                            </div>
                          </TableCell>
                          <TableCell className="capitalize">
                            {ag.recipientRole?.toLowerCase() || "N/A"}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">{ag.scopeOfWork || "N/A"}</TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={
                                ag.status === 'PENDING'
                                  ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                                  : "bg-blue-50 text-blue-700 border-blue-200"
                              }
                            >
                              {ag.status === 'PENDING' ? 'PENDING' : 'SIGNED BY RECIPIENT'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setProfessionalToSchedule(buildProfessionalFromAgreement(ag));
                                setIsEngagementDialogOpen(true);
                              }}
                            >
                              <FileText className="h-3 w-3 mr-1" />
                              Engagement
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-8 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
                  <p>No pending engagement requests</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="signed">
              {isAgreementsLoading ? (
                <div className="py-8 text-center text-muted-foreground">Loading agreements...</div>
              ) : signedAgreements.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Requester</TableHead>
                      <TableHead>Requester Role</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Recipient Role</TableHead>
                      <TableHead>Scope</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {signedAgreements.map((ag) => (
                      <TableRow key={ag.id}>
                        <TableCell className="font-medium">
                          <div>
                            <p>{ag.requesterFullName || "Unknown"}</p>
                            <p className="text-xs text-muted-foreground font-normal">{ag.requesterEmail}</p>
                          </div>
                        </TableCell>
                        <TableCell className="capitalize">
                          {ag.requesterRole?.toLowerCase() || "N/A"}
                        </TableCell>
                        <TableCell className="font-medium">
                          <div>
                            <p>{ag.recipientFullName || "Unknown"}</p>
                            <p className="text-xs text-muted-foreground font-normal">{ag.recipientEmail}</p>
                          </div>
                        </TableCell>
                        <TableCell className="capitalize">
                          {ag.recipientRole?.toLowerCase() || "N/A"}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">{ag.scopeOfWork || "N/A"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            SIGNED BY BOTH
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setProfessionalToSchedule(buildProfessionalFromAgreement(ag));
                              setIsEngagementDialogOpen(true);
                            }}
                          >
                            <FileText className="h-3 w-3 mr-1" />
                            Engagement
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-8 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
                  <p>No signed agreements</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Meeting Requests Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-primary" />
            <CardTitle>Meeting Requests</CardTitle>
          </div>
          <CardDescription>
            Manage your upcoming sessions and meeting invitations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="pending">
            <TabsList className="mb-4">
              <TabsTrigger value="pending" className="relative">
                Pending
                {filteredPendingRequests.length > 0 && (
                  <Badge className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center bg-primary text-primary-foreground text-[10px]">
                    {filteredPendingRequests.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="accepted" className="relative">
                Accepted
                {acceptedMeetingRequests.length > 0 && (
                  <Badge className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center bg-primary text-primary-foreground text-[10px]">
                    {acceptedMeetingRequests.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="pending">
              {filteredPendingRequests.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Host Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPendingRequests.map((meeting) => (
                      <TableRow key={meeting.meetingId}>
                        <TableCell className="font-medium">
                          {meeting.hostFullName || "N/A"}
                        </TableCell>
                        <TableCell className="capitalize">
                          {meeting.hostRole || "N/A"}
                        </TableCell>
                        <TableCell>
                          {/* {meeting.startTime ? format(new Date(meeting.startTime), "MMM d, yyyy h:mm a") : "N/A"} */}
                          {(() => {
                                      // Ensure the string is treated as UTC by appending 'Z' if it's missing 
                                      // and doesn't already have an offset.
                                      const dateStr = meeting.startTime.endsWith('Z') || meeting.startTime.includes('+') 
                                        ? meeting.startTime 
                                        : `${meeting.startTime}Z`;
                                      return new Date(dateStr).toLocaleString([], { 
                                        month: 'short', 
                                        day: 'numeric', 
                                        hour: 'numeric', 
                                        minute: '2-digit',
                                        timeZoneName: 'short'
                                      });
                                    })()}
                        </TableCell>
                        <TableCell>
                          {meeting.durationInMins} mins
                        </TableCell>
                        <TableCell>
                          {meeting.locationType || "N/A"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                            {meeting.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button 
                              size="sm" 
                              onClick={() => handleAcceptMeeting(meeting.meetingId)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              Accept
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => handleDeclineMeeting(meeting.meetingId)}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              Decline
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-8 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
                  <p>No pending meeting requests</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="accepted">
              {acceptedMeetingRequests.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Host Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {acceptedMeetingRequests.map((meeting) => (
                      <TableRow key={meeting.meetingId}>
                        <TableCell className="font-medium">
                          {meeting.hostFullName || "N/A"}
                        </TableCell>
                        <TableCell className="capitalize">
                          {meeting.hostRole || "N/A"}
                        </TableCell>
                        <TableCell>
                         {/*{meeting.startTime ? format(new Date(meeting.startTime), "MMM d, yyyy h:mm a") : "N/A"}*/}
                          {(() => {
                                      // Ensure the string is treated as UTC by appending 'Z' if it's missing 
                                      // and doesn't already have an offset.
                                      const dateStr = meeting.startTime.endsWith('Z') || meeting.startTime.includes('+') 
                                        ? meeting.startTime 
                                        : `${meeting.startTime}Z`;
                                      return new Date(dateStr).toLocaleString([], { 
                                        month: 'short', 
                                        day: 'numeric', 
                                        hour: 'numeric', 
                                        minute: '2-digit',
                                        timeZoneName: 'short'
                                      });
                                    })()}
                        </TableCell>
                        <TableCell>
                          {meeting.durationInMins} mins
                        </TableCell>
                        <TableCell>
                          {meeting.locationType || "N/A"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            {meeting.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleCancelMeeting(meeting.meetingId)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            Cancel
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-8 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
                  <p>No accepted meetings</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Header Banner */}
      <Card className="border-purple-200 bg-purple-50 dark:bg-purple-950 dark:border-purple-800">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-purple-100 dark:bg-purple-900">
              <Sparkles className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex-1">
              <h3 className="mb-1">AI-Powered Matching</h3>
              <p className="text-sm text-muted-foreground">
                Our intelligent algorithm analyzes over 50+
                factors including investment goals, risk
                tolerance, expertise, location, and
                communication style to find your perfect
                financial partnership.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilterOpen(!filterOpen)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filters Panel */}
      {filterOpen && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Filter Matches</CardTitle>
                {activeFilterCount > 0 && (
                  <CardDescription className="mt-1">
                    {activeFilterCount} active filter
                    {activeFilterCount !== 1 ? "s" : ""} •{" "}
                    {criteriaDescription}
                  </CardDescription>
                )}
              </div>
              <div className="flex gap-2">
                <Select onValueChange={handleApplyPreset}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Apply preset..." />
                  </SelectTrigger>
                  <SelectContent>
                    {FILTER_PRESETS.map((preset) => (
                      <SelectItem
                        key={preset.id}
                        value={preset.id}
                      >
                        {preset.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetFilters}
                  disabled={activeFilterCount === 0}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>
                  Minimum Match Score: {pendingMinMatchScore[0]}
                  %
                </Label>
                <Slider
                  value={pendingMinMatchScore}
                  onValueChange={setPendingMinMatchScore}
                  min={0}
                  max={100}
                  step={5}
                />
              </div>
              <div className="space-y-2">
                <Label>Specializations</Label>
                <MultiSelect
                  options={SPECIALIZATION_FILTER_OPTIONS.filter(
                    (opt) => opt.value !== "all",
                  ).map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  selected={pendingSpecializations}
                  onChange={setPendingSpecializations}
                  placeholder="Select specializations..."
                />
              </div>
              <div className="space-y-2">
                <Label>Certifications</Label>
                <MultiSelect
                  options={CERTIFICATION_FILTER_OPTIONS.map(
                    (option) => ({
                      value: option.value,
                      label: option.label,
                    }),
                  )}
                  selected={pendingCertifications}
                  onChange={setPendingCertifications}
                  placeholder="Select certifications..."
                />
              </div>
              <div className="space-y-2">
                <Label>Sectors</Label>
                <MultiSelect
                  options={SECTOR_FILTER_OPTIONS.map(
                    (option) => ({
                      value: option.value,
                      label: option.label,
                    }),
                  )}
                  selected={pendingSectors}
                  onChange={setPendingSectors}
                  placeholder="Select sectors..."
                />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Select
                  value={pendingLocation}
                  onValueChange={(value) => {
                    setPendingLocation(value);
                    if (value !== "custom") {
                      setPendingCustomLocation("");
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATION_FILTER_OPTIONS.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {pendingLocation === "custom" && (
                  <Input
                    placeholder="Enter custom location (e.g., Paris, IDF, FRA)"
                    value={pendingCustomLocation}
                    onChange={(e) =>
                      setPendingCustomLocation(e.target.value)
                    }
                    className="mt-2"
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label>Minimum Rating</Label>
                <Select
                  value={pendingMinRating}
                  onValueChange={setPendingMinRating}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RATING_FILTER_OPTIONS.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Apply Filters Button Section */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-2">
                {hasPendingChanges && (
                  <>
                    <Badge
                      variant="secondary"
                      className="bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300"
                    >
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Unsaved changes
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearPendingChanges}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Cancel
                    </Button>
                  </>
                )}
              </div>
              <Button
                onClick={handleApplyFilters}
                disabled={!hasPendingChanges}
                className="min-w-[120px]"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Apply Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* View Toggle */}
      <Tabs
        value={viewMode}
        onValueChange={(v) =>
          setViewMode(v as "advisor" | "investor" | "analyst")
        }
      >
        <TabsList className="grid w-full max-w-2xl grid-cols-3">
          <TabsTrigger value="advisor">
            <Shield className="h-4 w-4 mr-2" />
            Find Advisors
          </TabsTrigger>
          <TabsTrigger value="analyst">
            <TrendingUp className="h-4 w-4 mr-2" />
            Find Analysts
          </TabsTrigger>
          <TabsTrigger value="investor">
            <Users className="h-4 w-4 mr-2" />
            Find Investors
          </TabsTrigger>
        </TabsList>

        {/* Advisor Matches */}
        <TabsContent value="advisor" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3>Recommended Financial Advisors</h3>
              <p className="text-sm text-muted-foreground">
                {filteredAdvisors.length} matches based on your
                profile and preferences
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredAdvisors.map((advisor) => {
              const connection = connections.find(c => {
                const cReqId = String(c.requesterId || "");
                const cRecId = String(c.recipientId || c.professionalId || "");
                const currentUserId = String(userId || "");
                const advisorId = String(advisor.id || "");

                return (cReqId === currentUserId && cRecId === advisorId) || 
                       (cReqId === advisorId && cRecId === currentUserId);
              });
              const status = connection?.status;
              const currentPortfolioSharedStatus = connection ? (
                String(advisor.id) === String(connection.requesterId)
                  ? connection.portfolioSharedByRequester
                  : connection.portfolioSharedByRecipient
              ) : false;

              return (
                <Card
                  key={advisor.id}
                  className="hover:border-purple-300 transition-colors"
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <Avatar className="h-12 w-12">
                          <AvatarFallback>
                            {advisor.avatar}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-2">
                            <CardTitle className="text-base">
                              {advisor.name}
                            </CardTitle>
                            {status && (
                              <Badge 
                                variant="outline" 
                                className={cn(
                                  "text-[10px] px-1.5 py-0 h-5 font-medium uppercase",
                                  status === "ACTIVE" || status === "ACCEPTED" ? "bg-green-50 text-green-700 border-green-200" :
                                  status === "PENDING" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
                                  "bg-red-50 text-red-700 border-red-200"
                                )}
                              >
                                {status}
                              </Badge>
                            )}
                            {(status === "ACTIVE" || status === "ACCEPTED") && (
                              <Badge 
                                variant="outline" 
                                className={cn(
                                  "text-[10px] px-1.5 py-0 h-5 font-medium uppercase",
                                  currentPortfolioSharedStatus ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-slate-50 text-slate-700 border-slate-200"
                                )}
                              >
                                {currentPortfolioSharedStatus ? "Portfolio Shared" : "Portfolio Not Shared"}
                              </Badge>
                            )}
                          </div>
                        <p className="text-sm text-muted-foreground">
                          {advisor.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <StarRating
                            rating={advisor.rating}
                            size="sm"
                            showNumber={false}
                          />
                          <span className="text-sm font-medium">
                            {advisor.rating}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            ({advisor.reviewCount} reviews)
                          </span>
                          <button className="text-xs text-blue-600 hover:underline ml-2" onClick={() => setRatingTarget({ id: advisor.id, name: advisor.name, role: 'ADVISOR' })}>Rate</button>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge
                        variant="secondary"
                        className={
                          advisor.matchScore >= 95
                            ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                            : advisor.matchScore >= 90
                              ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                              : "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300"
                        }
                      >
                        {advisor.matchScore}% Match
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {advisor.bio}
                  </p>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{advisor.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                      <span>{advisor.experience}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span>AUM: {advisor.aum}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-muted-foreground" />
                      <span>Min: {advisor.minInvestment}</span>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground mb-2 block">
                      Specializations
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {advisor.specializations.map((spec) => (
                        <Badge key={spec} variant="outline">
                          {spec}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground mb-2 block">
                      Certifications
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {advisor.certifications.map((cert) => (
                        <Badge
                          key={cert}
                          variant="secondary"
                          className="gap-1 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                        >
                          <Award className="h-3 w-3" />
                          {cert}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Fee Structure */}
                  <div className="p-3 border rounded-lg bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2 mb-1">
                        <DollarSign className="h-4 w-4 text-green-600 dark:text-green-400" />
                        <Label className="text-xs font-medium text-green-900 dark:text-green-100">
                          Fee Structure
                        </Label>
                      </div>
                      
                      <div className="space-y-2">
                        {advisor.pricingInfo && advisor.pricingInfo.length > 0 ? (
                          advisor.pricingInfo.map((info, idx) => (
                            <div key={idx} className="flex flex-col border-b border-green-100 dark:border-green-900 pb-2 last:border-0 last:pb-0">
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-xs font-semibold text-green-800 dark:text-green-200">
                                  {info.type}
                                </span>
                                <Badge variant="outline" className="text-[10px] h-4 border-green-300 text-green-700 dark:text-green-300 px-1">
                                  {info.range}
                                </Badge>
                              </div>
                              <p className="text-[11px] leading-relaxed text-muted-foreground italic">
                                {info.details}
                              </p>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-muted-foreground italic">Contact for pricing details</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Dialog open={isConnectDialogOpen && selectedMatch?.id === advisor.id} onOpenChange={(open) => {
                      setIsConnectDialogOpen(open);
                      if (open) setSelectedMatch(advisor);
                    }}>
                      <DialogTrigger asChild>
                        <Button
                          className="flex-1"
                          onClick={() => {
                            setSelectedMatch(advisor);
                            setIsConnectDialogOpen(true);
                          }}
                          disabled={!!status}
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Connect
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>
                            Connect with {advisor.name}
                          </DialogTitle>
                          <DialogDescription>
                            Send a connection request to start
                            your financial partnership
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <Label htmlFor="message">
                              Message
                            </Label>
                            <Input
                              id="message"
                              placeholder="Introduce yourself and your financial goals..."
                              className="mt-2"
                              value={connectionMessage}
                              onChange={(e) => setConnectionMessage(e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>
                              Preferred Contact Method
                            </Label>
                            <MultiSelect
                              options={CONTACT_METHOD_OPTIONS}
                              selected={selectedContactMethods}
                              onChange={setSelectedContactMethods}
                              placeholder="Select contact methods..."
                              className="mt-2"
                            />
                          </div>
                          <div className="space-y-3 pt-2">
                            {userRole === 'INVESTOR' && (
                              <div className="flex items-center space-x-2">
                                <Checkbox 
                                  id="share-portfolio-advisor" 
                                  checked={sharePortfolio}
                                  onCheckedChange={(checked) => setSharePortfolio(!!checked)}
                                />
                                <Label htmlFor="share-portfolio-advisor" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                  Share Portfolio
                                </Label>
                              </div>
                            )}
                            <div className="flex items-center space-x-2">
                              <Checkbox 
                                id="share-profile-advisor" 
                                checked={shareProfile}
                                onCheckedChange={(checked) => setShareProfile(!!checked)}
                              />
                              <Label htmlFor="share-profile-advisor" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                Share Profile details
                              </Label>
                            </div>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setIsConnectDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button onClick={() => handleSendConnectionRequest(advisor.id)}>Send Request</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    {userRole === 'INVESTOR' && (
                      <Button
                        className="flex-1"
                        onClick={() => {
                          if (connection) {
                            setSharingConnectionId(connection.connectionId);
                            setSharingProfessionalId(advisor.id);
                            setSharingProfessionalName(advisor.name);
                            setIsCurrentlyShared(!!currentPortfolioSharedStatus);
                            setIsSharePortfolioDialogOpen(true);
                          }
                        }}
                        disabled={!(status === "ACTIVE" || status === "ACCEPTED")}
                      >
                        <Share2 className="h-4 w-4 mr-2" />
                        {currentPortfolioSharedStatus ? "Unshare Portfolio" : "Share Portfolio"}
                      </Button>
                    )}
                    {(userRole === 'ADVISOR' || userRole === 'ANALYST' || userRole === 'INVESTOR') && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setProfessionalToSchedule(advisor);
                          setIsEngagementDialogOpen(true);
                        }}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Engagement
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => {
                        setProfessionalToSchedule(advisor);
                        setIsScheduleDialogOpen(true);
                      }}
                    >
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      Schedule
                    </Button>
                    <Button variant="outline" size="icon">
                      <Heart className="h-4 w-4" />
                    </Button>
                    </div>
                    {(() => {
                    const upcomingMeetings = meetings.filter(m => (m.hostId === advisor.id || m.participantId === advisor.id));
                    return upcomingMeetings.length > 0 ? (
                      <div className="mt-3 space-y-2">
                        {upcomingMeetings.map((meeting) => (
                          <div key={meeting.id} className="p-2.5 bg-blue-50/50 border border-blue-100 rounded-lg text-xs">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="h-3.5 w-3.5 text-blue-600" />
                                <div className="flex flex-col">
                                  <span className="font-semibold text-blue-900">
                                    {(() => {
                                      // Ensure the string is treated as UTC by appending 'Z' if it's missing 
                                      // and doesn't already have an offset.
                                      const dateStr = meeting.startTime.endsWith('Z') || meeting.startTime.includes('+') 
                                        ? meeting.startTime 
                                        : `${meeting.startTime}Z`;
                                      return new Date(dateStr).toLocaleString([], { 
                                        month: 'short', 
                                        day: 'numeric', 
                                        hour: 'numeric', 
                                        minute: '2-digit',
                                        timeZoneName: 'short'
                                      });
                                    })()}
                                  </span>
                                  <span className="text-[10px] text-blue-700/70">Upcoming Meeting</span>
                                </div>
                              </div>
                              <Badge 
                                variant="outline" 
                                className={cn(
                                  "text-[10px] px-1.5 py-0 h-4.5 font-medium border-0",
                                  meeting.status === "ACCEPTED" 
                                    ? "bg-green-100 text-green-700" 
                                    : "bg-yellow-100 text-yellow-700"
                                )}
                              >
                                {meeting.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null;
                    })()}
                    </CardContent>              </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Analyst Matches */}
        <TabsContent value="analyst" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3>Recommended Financial Analysts</h3>
              <p className="text-sm text-muted-foreground">
                {filteredAnalysts.length} analysts matching your
                requirements
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredAnalysts.map((analyst) => {
              const connection = connections.find(c => {
                const cReqId = String(c.requesterId || "");
                const cRecId = String(c.recipientId || c.professionalId || "");
                const currentUserId = String(userId || "");
                const analystId = String(analyst.id || "");

                return (cReqId === currentUserId && cRecId === analystId) || 
                       (cReqId === analystId && cRecId === currentUserId);
              });
              const status = connection?.status;
              const currentPortfolioSharedStatus = connection ? (
                String(analyst.id) === String(connection.requesterId)
                  ? connection.portfolioSharedByRequester
                  : connection.portfolioSharedByRecipient
              ) : false;

              return (
                <Card
                  key={analyst.id}
                  className="hover:border-green-300 transition-colors"
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <Avatar className="h-12 w-12">
                          <AvatarFallback>
                            {analyst.avatar}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-2">
                            <CardTitle className="text-base">
                              {analyst.name}
                            </CardTitle>
                            {status && (
                              <Badge 
                                variant="outline" 
                                className={cn(
                                  "text-[10px] px-1.5 py-0 h-5 font-medium uppercase",
                                  status === "ACTIVE" || status === "ACCEPTED" ? "bg-green-50 text-green-700 border-green-200" :
                                  status === "PENDING" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
                                  "bg-red-50 text-red-700 border-red-200"
                                )}
                              >
                                {status}
                              </Badge>
                            )}
                            {(status === "ACTIVE" || status === "ACCEPTED") && (
                              <Badge 
                                variant="outline" 
                                className={cn(
                                  "text-[10px] px-1.5 py-0 h-5 font-medium uppercase",
                                  currentPortfolioSharedStatus ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-slate-50 text-slate-700 border-slate-200"
                                )}
                              >
                                {currentPortfolioSharedStatus ? "Portfolio Shared" : "Portfolio Not Shared"}
                              </Badge>
                            )}
                          </div>
                        <p className="text-sm text-muted-foreground">
                          {analyst.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <StarRating
                            rating={analyst.rating}
                            size="sm"
                            showNumber={false}
                          />
                          <span className="text-sm font-medium">
                            {analyst.rating}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            ({analyst.reviewCount} reviews)
                          </span>
                          <button className="text-xs text-blue-600 hover:underline ml-2" onClick={() => setRatingTarget({ id: analyst.id, name: analyst.name, role: 'ANALYST' })}>Rate</button>
                        </div>
                      </div>
                    </div>
                    <Badge
                      variant="secondary"
                      className={
                        analyst.matchScore >= 95
                          ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                          : analyst.matchScore >= 90
                            ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                            : "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300"
                      }
                    >
                      {analyst.matchScore}% Match
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {analyst.bio}
                  </p>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{analyst.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                      <span>{analyst.experience}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <GraduationCap className="h-4 w-4 text-muted-foreground" />
                      <span>
                        {analyst.publications} Publications
                      </span>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground mb-2 block">
                      Specializations
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {analyst.specializations.map((spec) => (
                        <Badge key={spec} variant="outline">
                          {spec}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground mb-2 block">
                      Research Focus
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {analyst.researchFocus.map((focus) => (
                        <Badge key={focus} variant="secondary">
                          {focus}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground mb-2 block">
                      Certifications
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {analyst.certifications.map((cert) => (
                        <Badge
                          key={cert}
                          variant="secondary"
                          className="gap-1 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                        >
                          <Award className="h-3 w-3" />
                          {cert}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Fee Structure */}
                  <div className="p-3 border rounded-lg bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2 mb-1">
                        <DollarSign className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        <Label className="text-xs font-medium text-blue-900 dark:text-blue-100">
                          Fee Structure
                        </Label>
                      </div>
                      
                      <div className="space-y-2">
                        {analyst.pricingInfo && analyst.pricingInfo.length > 0 ? (
                          analyst.pricingInfo.map((info, idx) => (
                            <div key={idx} className="flex flex-col border-b border-blue-100 dark:border-blue-900 pb-2 last:border-0 last:pb-0">
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-xs font-semibold text-blue-800 dark:text-blue-200">
                                  {info.type}
                                </span>
                                <Badge variant="outline" className="text-[10px] h-4 border-blue-300 text-blue-700 dark:text-blue-300 px-1">
                                  {info.range}
                                </Badge>
                              </div>
                              <p className="text-[11px] leading-relaxed text-muted-foreground italic">
                                {info.details}
                              </p>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-muted-foreground italic">Contact for pricing details</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Dialog open={isConnectDialogOpen && selectedMatch?.id === analyst.id} onOpenChange={(open) => {
                      setIsConnectDialogOpen(open);
                      if (open) setSelectedMatch(analyst);
                    }}>
                      <DialogTrigger asChild>
                        <Button
                          className="flex-1"
                          onClick={() => {
                            setSelectedMatch(analyst);
                            setIsConnectDialogOpen(true);
                          }}
                          disabled={!!status}
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Connect
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>
                            Connect with {analyst.name}
                          </DialogTitle>
                          <DialogDescription>
                            Request collaboration or research
                            insights
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <Label htmlFor="request">
                              Your Request
                            </Label>
                            <Input
                              id="request"
                              placeholder="What would you like to collaborate on?"
                              className="mt-2"
                              value={connectionMessage}
                              onChange={(e) => setConnectionMessage(e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>
                              Preferred Contact Method
                            </Label>
                            <MultiSelect
                              options={CONTACT_METHOD_OPTIONS}
                              selected={selectedContactMethods}
                              onChange={setSelectedContactMethods}
                              placeholder="Select contact methods..."
                              className="mt-2"
                            />
                          </div>
                          <div className="space-y-3 pt-2">
                            {userRole === 'INVESTOR' && (
                              <div className="flex items-center space-x-2">
                                <Checkbox 
                                  id="share-portfolio-analyst" 
                                  checked={sharePortfolio}
                                  onCheckedChange={(checked) => setSharePortfolio(!!checked)}
                                />
                                <Label htmlFor="share-portfolio-analyst" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                  Share Portfolio
                                </Label>
                              </div>
                            )}
                            <div className="flex items-center space-x-2">
                              <Checkbox 
                                id="share-profile-analyst" 
                                checked={shareProfile}
                                onCheckedChange={(checked) => setShareProfile(!!checked)}
                              />
                              <Label htmlFor="share-profile-analyst" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                Share Profile details
                              </Label>
                            </div>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setIsConnectDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button onClick={() => handleSendConnectionRequest(analyst.id)}>Send Request</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    {userRole === 'INVESTOR' && (
                      <Button
                        className="flex-1"
                        onClick={() => {
                          if (connection) {
                            setSharingConnectionId(connection.connectionId);
                            setSharingProfessionalId(analyst.id);
                            setSharingProfessionalName(analyst.name);
                            setIsCurrentlyShared(!!currentPortfolioSharedStatus);
                            setIsSharePortfolioDialogOpen(true);
                          }
                        }}
                        disabled={!(status === "ACTIVE" || status === "ACCEPTED")}
                      >
                        <Share2 className="h-4 w-4 mr-2" />
                        {currentPortfolioSharedStatus ? "Unshare Portfolio" : "Share Portfolio"}
                      </Button>
                    )}
                    {(userRole === 'ADVISOR' || userRole === 'ANALYST' || userRole === 'INVESTOR') && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setProfessionalToSchedule(analyst);
                          setIsEngagementDialogOpen(true);
                        }}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Engagement
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => {
                        setProfessionalToSchedule(analyst);
                        setIsScheduleDialogOpen(true);
                      }}
                    >
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      Schedule
                    </Button>
                    <Button variant="outline" size="icon">
                      <Heart className="h-4 w-4" />
                    </Button>
                    </div>
                    {(() => {
                    const upcomingMeetings = meetings.filter(m => (m.hostId === analyst.id || m.participantId === analyst.id));
                    return upcomingMeetings.length > 0 ? (
                      <div className="mt-3 space-y-2">
                        {upcomingMeetings.map((meeting) => (
                          <div key={meeting.id} className="p-2.5 bg-blue-50/50 border border-blue-100 rounded-lg text-xs">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="h-3.5 w-3.5 text-blue-600" />
                                <div className="flex flex-col">
                                  <span className="font-semibold text-blue-900">
                                    {(() => {
                                      // Ensure the string is treated as UTC by appending 'Z' if it's missing 
                                      // and doesn't already have an offset.
                                      const dateStr = meeting.startTime.endsWith('Z') || meeting.startTime.includes('+') 
                                        ? meeting.startTime 
                                        : `${meeting.startTime}Z`;
                                      return new Date(dateStr).toLocaleString([], { 
                                        month: 'short', 
                                        day: 'numeric', 
                                        hour: 'numeric', 
                                        minute: '2-digit',
                                        timeZoneName: 'short'
                                      });
                                    })()}
                                  </span>
                                  <span className="text-[10px] text-blue-700/70">Upcoming Meeting</span>
                                </div>
                              </div>
                              <Badge 
                                variant="outline" 
                                className={cn(
                                  "text-[10px] px-1.5 py-0 h-4.5 font-medium border-0",
                                  meeting.status === "ACCEPTED" 
                                    ? "bg-green-100 text-green-700" 
                                    : "bg-yellow-100 text-yellow-700"
                                )}
                              >
                                {meeting.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null;
                    })()}
                    </CardContent>              </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Investor Matches */}
        <TabsContent value="investor" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3>Recommended Investors</h3>
              <p className="text-sm text-muted-foreground">
                {filteredInvestors.length} potential clients
                matching your expertise
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredInvestors.map((investor) => {
              const connection = connections.find(c => {
                const cReqId = String(c.requesterId || "");
                const cRecId = String(c.recipientId || c.professionalId || "");
                const currentUserId = String(userId || "");
                const investorId = String(investor.id || "");

                return (cReqId === currentUserId && cRecId === investorId) || 
                       (cReqId === investorId && cRecId === currentUserId);
              });
              const status = connection?.status;
              const currentPortfolioSharedStatus = connection ? (
                String(investor.id) === String(connection.requesterId)
                  ? connection.portfolioSharedByRequester
                  : connection.portfolioSharedByRecipient
              ) : false;

              return (
                <Card
                  key={investor.id}
                  className="hover:border-blue-300 transition-colors"
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <Avatar className="h-12 w-12">
                          <AvatarFallback>
                            {investor.avatar}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-2">
                            <CardTitle className="text-base">
                              {investor.name}
                            </CardTitle>
                            {status && (
                              <Badge 
                                variant="outline" 
                                className={cn(
                                  "text-[10px] px-1.5 py-0 h-5 font-medium uppercase",
                                  status === "ACTIVE" || status === "ACCEPTED" ? "bg-green-50 text-green-700 border-green-200" :
                                  status === "PENDING" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
                                  "bg-red-50 text-red-700 border-red-200"
                                )}
                              >
                                {status}
                              </Badge>
                            )}
                            {(status === "ACTIVE" || status === "ACCEPTED") && (
                              <Badge 
                                variant="outline" 
                                className={cn(
                                  "text-[10px] px-1.5 py-0 h-5 font-medium uppercase",
                                  currentPortfolioSharedStatus ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-slate-50 text-slate-700 border-slate-200"
                                )}
                              >
                                {currentPortfolioSharedStatus ? "Portfolio Shared" : "Portfolio Not Shared"}
                              </Badge>
                            )}
                          </div>
                        <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {investor.location}
                        </div>
                      </div>
                    </div>
                    <Badge
                      variant="secondary"
                      className={
                        investor.matchScore >= 95
                          ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                          : investor.matchScore >= 90
                            ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                            : "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300"
                      }
                    >
                      {investor.matchScore}% Match
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">
                      Looking For
                    </Label>
                    <p className="text-sm">
                      {investor.lookingFor}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Portfolio Value
                      </Label>
                      <p className="mt-1">
                        {investor.portfolioValue}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Risk Tolerance
                      </Label>
                      <p className="mt-1">
                        {investor.riskTolerance}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Time Horizon
                      </Label>
                      <p className="mt-1">
                        {investor.investmentHorizon}
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground mb-2 block">
                      Investment Goals
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {investor.investmentGoals.map((goal) => (
                        <Badge key={goal} variant="outline">
                          {goal}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground mb-2 block">
                      Preferred Sectors
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {investor.preferredSectors.map(
                        (sector) => (
                          <Badge
                            key={sector}
                            variant="secondary"
                          >
                            {sector}
                          </Badge>
                        ),
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Dialog open={isConnectDialogOpen && selectedMatch?.id === investor.id} onOpenChange={(open) => {
                      setIsConnectDialogOpen(open);
                      if (open) setSelectedMatch(investor);
                    }}>
                      <DialogTrigger asChild>
                        <Button
                          className="flex-1"
                          onClick={() => {
                            setSelectedMatch(investor);
                            setIsConnectDialogOpen(true);
                          }}
                          disabled={!!status}
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Reach Out
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>
                            Connect with {investor.name}
                          </DialogTitle>
                          <DialogDescription>
                            Send a connection request to showcase your
                            expertise and services
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <Label htmlFor="message">
                              Your Message
                            </Label>
                            <Input
                              id="message"
                              placeholder="Explain how you can help achieve their goals..."
                              className="mt-2"
                              value={connectionMessage}
                              onChange={(e) => setConnectionMessage(e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>
                              Preferred Contact Method
                            </Label>
                            <MultiSelect
                              options={CONTACT_METHOD_OPTIONS}
                              selected={selectedContactMethods}
                              onChange={setSelectedContactMethods}
                              placeholder="Select contact methods..."
                              className="mt-2"
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setIsConnectDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button onClick={() => handleSendConnectionRequest(investor.id)}>Send Request</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    {userRole === 'INVESTOR' && (
                      <Button
                        className="flex-1"
                        onClick={() => {
                          if (connection) {
                            setSharingConnectionId(connection.connectionId);
                            setSharingProfessionalId(investor.id);
                            setSharingProfessionalName(investor.name);
                            setIsCurrentlyShared(!!currentPortfolioSharedStatus);
                            setIsSharePortfolioDialogOpen(true);
                          }
                        }}
                        disabled={!(status === "ACTIVE" || status === "ACCEPTED")}
                      >
                        <Share2 className="h-4 w-4 mr-2" />
                        {currentPortfolioSharedStatus ? "Unshare Portfolio" : "Share Portfolio"}
                      </Button>
                    )}
                    {(userRole === 'ADVISOR' || userRole === 'ANALYST' || userRole === 'INVESTOR') && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setProfessionalToSchedule(investor);
                          setIsEngagementDialogOpen(true);
                        }}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Engagement
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => {
                        setProfessionalToSchedule(investor);
                        setIsScheduleDialogOpen(true);
                      }}
                    >
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      Propose Meeting
                    </Button>
                    <Button variant="outline" size="icon">
                      <Heart className="h-4 w-4" />
                    </Button>
                    </div>
                    {(() => {
                    const upcomingMeetings = meetings.filter(m => (m.hostId === investor.id || m.participantId === investor.id));
                    return upcomingMeetings.length > 0 ? (
                      <div className="mt-3 space-y-2">
                        {upcomingMeetings.map((meeting) => (
                          <div key={meeting.id} className="p-2.5 bg-blue-50/50 border border-blue-100 rounded-lg text-xs">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="h-3.5 w-3.5 text-blue-600" />
                                <div className="flex flex-col">
                                  <span className="font-semibold text-blue-900">
                                    {(() => {
                                      // Ensure the string is treated as UTC by appending 'Z' if it's missing 
                                      // and doesn't already have an offset.
                                      const dateStr = meeting.startTime.endsWith('Z') || meeting.startTime.includes('+') 
                                        ? meeting.startTime 
                                        : `${meeting.startTime}Z`;
                                      return new Date(dateStr).toLocaleString([], { 
                                        month: 'short', 
                                        day: 'numeric', 
                                        hour: 'numeric', 
                                        minute: '2-digit',
                                        timeZoneName: 'short'
                                      });
                                    })()}
                                  </span>
                                  <span className="text-[10px] text-blue-700/70">Upcoming Meeting</span>
                                </div>
                              </div>
                              <Badge 
                                variant="outline" 
                                className={cn(
                                  "text-[10px] px-1.5 py-0 h-4.5 font-medium border-0",
                                  meeting.status === "ACCEPTED" 
                                    ? "bg-green-100 text-green-700" 
                                    : "bg-yellow-100 text-yellow-700"
                                )}
                              >
                                {meeting.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null;
                    })()}
                    </CardContent>              </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Why This Match Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            How We Calculate Match Scores
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-5 w-5 text-blue-600" />
                <span className="text-sm">Goal Alignment</span>
              </div>
              <Progress value={85} className="mb-2" />
              <p className="text-xs text-muted-foreground">
                Matches investment goals with advisor expertise
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                <span className="text-sm">Risk Profile</span>
              </div>
              <Progress value={92} className="mb-2" />
              <p className="text-xs text-muted-foreground">
                Aligns risk tolerance with strategy
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <GraduationCap className="h-5 w-5 text-purple-600" />
                <span className="text-sm">Expertise Match</span>
              </div>
              <Progress value={88} className="mb-2" />
              <p className="text-xs text-muted-foreground">
                Specialization fits investor needs
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-5 w-5 text-orange-600" />
                <span className="text-sm">
                  Communication Style
                </span>
              </div>
              <Progress value={78} className="mb-2" />
              <p className="text-xs text-muted-foreground">
                Compatible working preferences
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <ScheduleMeetingDialog
        professional={professionalToSchedule}
        isOpen={isScheduleDialogOpen}
        onOpenChange={setIsScheduleDialogOpen}
        onScheduleSuccess={fetchMeetings}
      />

      <Dialog open={!!ratingTarget} onOpenChange={(open) => { if (!open) setRatingTarget(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Rate {ratingTarget?.name}</DialogTitle>
            <DialogDescription>Share your experience working with this professional</DialogDescription>
          </DialogHeader>
          {ratingTarget && (
            <ReviewForm
              revieweeId={ratingTarget.id}
              revieweeName={ratingTarget.name}
              revieweeRole={ratingTarget.role === 'ANALYST' ? 'ANALYST' : 'ADVISOR'}
              onSuccess={() => { setRatingTarget(null); toast.success("Review submitted"); }}
              onCancel={() => setRatingTarget(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <EngagementAgreementDialog
        professional={professionalToSchedule}
        isOpen={isEngagementDialogOpen}
        onOpenChange={setIsEngagementDialogOpen}
      />

      <AlertDialog open={isSharePortfolioDialogOpen} onOpenChange={setIsSharePortfolioDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isCurrentlyShared ? "Unshare Portfolio?" : "Share Portfolio?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isCurrentlyShared 
                ? `Are you sure you want to stop sharing your portfolio with ${sharingProfessionalName}?`
                : `Would you like to share your portfolio with ${sharingProfessionalName}? This will allow them to view your holdings and performance.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No</AlertDialogCancel> 
            <AlertDialogAction onClick={handleSharePortfolio}>Yes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}